import {
  ApiError,
  AssessmentMethodType,
  GET_ASSESSMENTS_ENTRIES_QUERY,
  useUpsertDeleteAssessmentEntryMutation,
} from '@schooly/api';
import {
  CellContext,
  enhanceGridCellHandler,
  GridCellMiddleware,
} from '@schooly/components/data-grid';
import { useQueryClient } from '@tanstack/react-query';

import { convertToAssessmentEntry } from '../../../../../components/uikit-components/AssessmentEntryMethods/utils';
import { AssessmentCellState, UpdateAssessmentEntryParams } from '../types';

export type ScoreUpdateParams = {
  cellId: string;
  rowId: string;
  field: string;
  studentId: string;
  methodId: string;
  assessmentId: string;
  score: number | null;
  groupId: string;
  categoryId?: string;
};
type ScoreUpdateHandlerProps = {
  cellContext: CellContext<AssessmentCellState>;
  middlewareConfig: GridCellMiddleware<any, any, AssessmentCellState>[];
  updateLocalState: (params: UpdateAssessmentEntryParams & { categoryId: string }) => void;
  onUpdateError?: (error: ApiError) => void;
};

export const useScoreUpdateHandler = ({
  cellContext,
  middlewareConfig,
  updateLocalState,
  onUpdateError,
}: ScoreUpdateHandlerProps) => {
  const queryClient = useQueryClient();
  const upsertDeleteAssessmentEntry = useUpsertDeleteAssessmentEntryMutation();

  const updateScore = async (params: UpdateAssessmentEntryParams & { categoryId?: string }) => {
    const { studentId, methodId, entry, groupId, categoryId = '' } = params;
    const score = entry && 'score' in entry ? entry.score : null;

    try {
      const res = await upsertDeleteAssessmentEntry.mutateAsync({
        method_id: methodId,
        recipient_relation_id: studentId,
        data: { group_id: groupId, score },
      });

      queryClient.invalidateQueries([GET_ASSESSMENTS_ENTRIES_QUERY]);

      updateLocalState({
        ...params,
        entry: res.assessment_entry ? convertToAssessmentEntry(res.assessment_entry) : null,
        categoryId,
      });
    } catch (err) {
      onUpdateError?.(err as ApiError);
      throw err;
    }
  };

  const enhancedHandler = enhanceGridCellHandler(updateScore, cellContext, middlewareConfig);

  return ({
    cellId,
    rowId,
    field,
    studentId,
    methodId,
    assessmentId,
    score,
    groupId,
    categoryId,
  }: ScoreUpdateParams): Promise<void> => {
    const updateParams: UpdateAssessmentEntryParams & { categoryId?: string } = {
      studentId,
      assessmentId,
      methodId,
      entry:
        score !== null
          ? { method: { id: methodId, type: AssessmentMethodType.Score }, score }
          : null,
      groupId,
      methodType: AssessmentMethodType.Score,
      categoryId,
    };

    return enhancedHandler.withCellId(cellId, rowId, field)(updateParams);
  };
};
